import { useCallback, useState } from "react";
import { COMPILATION_MAX_RANGE_DAYS, COMPILATION_MAX_RANGE_MS } from "../../../client-messages/compilation";
import { combineDateAndTime } from "./date-range";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RequestCompilationFormValues {
  readonly cameraId: string | null;
  readonly startDate: Date | null;
  readonly startTime: string;
  readonly endDate: Date | null;
  readonly endTime: string;
}

export interface UseRequestCompilationFormOptions {
  readonly onSubmit: (values: RequestCompilationFormValues) => void;
  readonly loading?: boolean | undefined;
  readonly disabled?: boolean | undefined;
}

export interface FormField<T> {
  readonly value: T;
  readonly error: string | undefined;
  readonly onChange: (value: T) => void;
}

export interface UseRequestCompilationFormReturn {
  readonly camera: FormField<string | null>;
  readonly startDate: FormField<Date | null>;
  readonly startTime: FormField<string>;
  readonly endDate: FormField<Date | null>;
  readonly endTime: FormField<string>;
  readonly canSubmit: boolean;
  readonly onSubmit: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function maskTimeInput(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}:${digits.slice(2)}`;
}

function isValidTime(time: string): boolean {
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) return false;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  return hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useRequestCompilationForm({
  onSubmit,
  loading,
  disabled,
}: UseRequestCompilationFormOptions): UseRequestCompilationFormReturn {
  const [cameraId, setCameraId] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [startTime, setStartTime] = useState("00:00");
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [endTime, setEndTime] = useState("23:59");

  const handleStartTimeChange = useCallback((text: string) => {
    setStartTime(maskTimeInput(text));
  }, []);

  const handleEndTimeChange = useCallback((text: string) => {
    setEndTime(maskTimeInput(text));
  }, []);

  const startTimeError =
    startTime.length > 0 && startTime.length === 5 && !isValidTime(startTime) ? "Invalid time" : undefined;

  const isInvertedRange =
    startDate !== null &&
    endDate !== null &&
    isValidTime(startTime) &&
    isValidTime(endTime) &&
    startDate.getTime() === endDate.getTime() &&
    startTime >= endTime;

  // Mirrors the backend's own refine check on `RequestCompilationBody` (see
  // `client-messages/compilation.ts`) — the date pickers' min/max props keep
  // most out-of-range selections from being made in the first place, but
  // time-of-day alone can still push a same-day-span combination over the
  // limit, so this re-checks the exact combined duration.
  const rangeExceedsMax =
    startDate !== null &&
    endDate !== null &&
    isValidTime(startTime) &&
    isValidTime(endTime) &&
    combineDateAndTime(endDate, endTime).getTime() - combineDateAndTime(startDate, startTime).getTime() >
      COMPILATION_MAX_RANGE_MS;

  const endTimeError =
    endTime.length > 0 && endTime.length === 5 && !isValidTime(endTime) ? "Invalid time"
    : isInvertedRange ? "Must be after start time"
    : rangeExceedsMax ? `Range must be ${String(COMPILATION_MAX_RANGE_DAYS)} days or less`
    : undefined;

  const canSubmit =
    cameraId !== null &&
    startDate !== null &&
    endDate !== null &&
    isValidTime(startTime) &&
    isValidTime(endTime) &&
    !isInvertedRange &&
    !rangeExceedsMax &&
    !disabled &&
    !loading;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit({ cameraId, startDate, startTime, endDate, endTime });
  }, [canSubmit, onSubmit, cameraId, startDate, startTime, endDate, endTime]);

  return {
    camera: { value: cameraId, error: undefined, onChange: setCameraId },
    startDate: { value: startDate, error: undefined, onChange: setStartDate },
    startTime: { value: startTime, error: startTimeError, onChange: handleStartTimeChange },
    endDate: { value: endDate, error: undefined, onChange: setEndDate },
    endTime: { value: endTime, error: endTimeError, onChange: handleEndTimeChange },
    canSubmit,
    onSubmit: handleSubmit,
  };
}
